import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import { deliverSsoAnswer, redeemSsoAnswer, takeStoredSsoRequest } from '@lib/sso';
import { Button, CircularProgress, CssBaseline, Stack, Typography } from '@mui/material';
import { useSsoSignInMutation } from '@store/api/authEndpoints';
import AppTheme from '@theme/theme';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * `working` until the answer is handed over or redeemed; `done` when the page that asked has
 * it and this window only failed to close itself; `orphan` when nobody is waiting for it.
 */
type Status = 'working' | 'done' | 'orphan';

/**
 * Where the server sends the browser once a sign-in through the identity provider is over.
 * Usually this is the window the sign-in screen opened: it hands the answer to that page and
 * closes. When no window could be opened, the tab went to the provider itself and comes back
 * here; then this page redeems the code and goes on into the app.
 */
const SsoCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [ssoSignIn] = useSsoSignInMutation();
  const [status, setStatus] = useState<Status>('working');
  // A code is good once. In development React mounts twice and would spend it twice.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const answer = {
      state: searchParams.get('state') ?? undefined,
      code: searchParams.get('code') ?? undefined,
      error: searchParams.get('error') ?? undefined,
    };

    // The code works for a minute and once, and still has no place in the history.
    window.history.replaceState(null, '', ROUTE_PATHS.SSO_CALLBACK);

    const stored = takeStoredSsoRequest();

    // This tab went to the provider itself, so it is the one that redeems.
    if (stored && stored.state === answer.state) {
      void redeemSsoAnswer(answer, stored.verifier, (input) => ssoSignIn(input).unwrap()).then((ssoError) => {
        if (ssoError) navigate(ROUTE_PATHS.LOGIN, { replace: true, state: { ssoError } });
        else navigate(ROUTE_PATHS.HOME, { replace: true });
      });

      return;
    }

    void deliverSsoAnswer(answer).then((taken) => {
      if (taken) window.close();
      setStatus(taken ? 'done' : 'orphan');
    });
  }, [navigate, searchParams, ssoSignIn]);

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <Stack component="main" alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: '100vh', p: 3 }}>
        {status === 'working' && <CircularProgress size={28} />}
        <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: 420 }}>
          {t(status === 'working' ? 'SignIn_Sso_Completing' : status === 'done' ? 'SignIn_Sso_CloseWindow' : 'SignIn_Sso_Orphan')}
        </Typography>
        {/* Where this is a tab of its own and not a window a sign-in opened, the way back. */}
        {status === 'orphan' && (
          <Button variant="outlined" onClick={() => navigate(ROUTE_PATHS.LOGIN, { replace: true })}>{t('SignIn')}</Button>
        )}
      </Stack>
    </AppTheme>
  );
};

export default SsoCallbackPage;
